import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import LegalFooter from "../components/LegalFooter";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function SignUp() {
  const { signUp } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  // SMS consent MUST default to false — user has to actively opt in.
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (smsConsent && !phone.trim()) {
      toast.error("Enter your mobile phone number to opt in to SMS.");
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, {
        phone: phone.trim() || null,
        sms_consent: smsConsent,
      });
      toast.success("Account created");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="signup-screen">
      <header className="px-6 py-5">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="signup-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </header>
      <main className="flex-1 flex items-start justify-center px-6 pt-6 pb-16">
        <div className="w-full max-w-md">
          <span className="brand-wordmark text-xs text-muted-foreground">FINALLOOK</span>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-3 mb-2">Create your account</h1>
          <p className="text-sm text-muted-foreground mb-8">Private by design. Your reviews are only visible to you.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11"
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="h-11"
                data-testid="signup-password-input"
              />
              <p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                Mobile phone number <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+15551234567"
                className="h-11"
                data-testid="signup-phone-input"
              />
              <p className="text-xs text-muted-foreground">
                Required only if you want to opt in to SMS below. Use international format (e.g., +15551234567).
              </p>
            </div>

            <div
              className="rounded-lg border border-border/70 p-4 bg-secondary/30 space-y-3"
              data-testid="signup-sms-consent-block"
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  id="sms-consent"
                  checked={smsConsent}
                  onCheckedChange={(v) => setSmsConsent(v === true)}
                  className="mt-0.5"
                  data-testid="signup-sms-consent-checkbox"
                />
                <div className="flex-1 space-y-1">
                  <label
                    htmlFor="sms-consent"
                    className="text-sm font-medium leading-snug cursor-pointer select-none"
                    data-testid="signup-sms-consent-label"
                  >
                    SMS updates (optional)
                  </label>
                  <p
                    className="text-xs text-muted-foreground leading-relaxed"
                    data-testid="signup-sms-consent-disclosure"
                  >
                    I agree to receive SMS messages from FINALLOOK at the phone number provided.
                    Message and data rates may apply. Message frequency varies. Reply STOP to opt
                    out and HELP for help. Consent is not a condition of purchase.
                  </p>
                  <p
                    className="text-xs text-muted-foreground leading-relaxed"
                    data-testid="signup-sms-consent-links"
                  >
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2 hover:text-primary/80"
                      data-testid="signup-sms-privacy-link"
                    >
                      Privacy Policy
                    </Link>
                    <span className="mx-1 text-border">·</span>
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-2 hover:text-primary/80"
                      data-testid="signup-sms-terms-link"
                    >
                      Terms of Service
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              data-testid="signup-submit-btn"
            >
              {loading ? "Creating account…" : "Create Account"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/signin" className="text-primary font-medium hover:underline" data-testid="signup-to-signin-link">
              Sign in
            </Link>
          </p>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
